import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StandingDto } from '@shared/api/hooks/useStandings';
import { goalDifference, netPoints } from '../sort';

/** Editable working copy of a standings row + per-row UI state. */
export interface DraftRow {
  /** Stable client-side key (rows have no server id exposed in DTO; we mint a uuid). */
  uid: string;
  position: number;
  teamName: string;
  teamLogo: string | null;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifference: number;
  /** Punti netti (3·V + N − PEN) di default, manual override se pointsLocked. */
  points: number;
  penalty: number;
  isOwnTeam: boolean;
  /** Se true, points non viene auto-ricalcolato (caso walkover/forfait/TFN). PEN resta editabile. */
  pointsLocked: boolean;
  /** Marker visivo: riga aggiunta in questo dirty buffer ma non ancora salvata. */
  isNew: boolean;
  /** Marker visivo: riga marcata per delete (filtrata fuori al save). */
  isDeleted: boolean;
}

function mintUid(): string {
  return `r_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
}

export function dtoToDraft(d: StandingDto): DraftRow {
  return {
    uid: mintUid(),
    position: d.position,
    teamName: d.teamName,
    teamLogo: d.teamLogo,
    played: d.played,
    won: d.won,
    drawn: d.drawn,
    lost: d.lost,
    goalsFor: d.goalsFor,
    goalsAgainst: d.goalsAgainst,
    goalDifference: d.goalDifference,
    points: d.points,
    penalty: d.penalty,
    isOwnTeam: d.isOwnTeam,
    pointsLocked: d.points !== 3 * d.won + d.drawn - d.penalty,
    isNew: false,
    isDeleted: false,
  };
}

function recompute(r: DraftRow): DraftRow {
  const gd = goalDifference(r);
  const next: DraftRow = { ...r, goalDifference: gd };
  if (!r.pointsLocked) next.points = netPoints(r);
  return next;
}

/**
 * Dirty-buffer hook: keeps editable copy of server rows, tracks dirty status row-by-row,
 * exposes mutators with auto-derive for points/DR, and provides reset-to-baseline.
 */
export function useDirtyBuffer(serverRows: StandingDto[] | undefined) {
  const baselineRef = useRef<DraftRow[]>([]);
  const [draft, setDraft] = useState<DraftRow[]>([]);
  const [dirtyUids, setDirtyUids] = useState<Set<string>>(new Set());

  // Hydrate baseline + draft from server fetch. Resetting clears dirty state.
  useEffect(() => {
    if (!serverRows) return;
    const drafts = serverRows.map(dtoToDraft);
    baselineRef.current = drafts.map((d) => ({ ...d }));
    setDraft(drafts);
    setDirtyUids(new Set());
  }, [serverRows]);

  const markDirty = useCallback((uid: string) => {
    setDirtyUids((prev) => {
      if (prev.has(uid)) return prev;
      const next = new Set(prev);
      next.add(uid);
      return next;
    });
  }, []);

  const patchRow = useCallback(
    (uid: string, patch: Partial<DraftRow>) => {
      setDraft((prev) =>
        prev.map((r) => {
          if (r.uid !== uid) return r;
          const merged = { ...r, ...patch };
          return recompute(merged);
        }),
      );
      markDirty(uid);
    },
    [markDirty],
  );

  const addRow = useCallback(
    (initial: Partial<DraftRow> = {}) => {
      const uid = mintUid();
      setDraft((prev) => {
        const maxPos = prev.reduce((m, r) => Math.max(m, r.position), 0);
        const blank: DraftRow = {
          uid,
          position: maxPos + 1,
          teamName: initial.teamName ?? '',
          teamLogo: initial.teamLogo ?? null,
          played: 0,
          won: 0,
          drawn: 0,
          lost: 0,
          goalsFor: 0,
          goalsAgainst: 0,
          goalDifference: 0,
          points: 0,
          penalty: 0,
          isOwnTeam: initial.isOwnTeam ?? false,
          pointsLocked: false,
          isNew: true,
          isDeleted: false,
          ...initial,
        };
        return [...prev, recompute(blank)];
      });
      markDirty(uid);
      return uid;
    },
    [markDirty],
  );

  const deleteRow = useCallback(
    (uid: string) => {
      setDraft((prev) => prev.map((r) => (r.uid === uid ? { ...r, isDeleted: true } : r)));
      markDirty(uid);
    },
    [markDirty],
  );

  const setTenant = useCallback(
    (uid: string) => {
      setDraft((prev) =>
        prev.map((r) => {
          const wasTenant = r.isOwnTeam;
          const willBeTenant = r.uid === uid;
          if (wasTenant === willBeTenant) return r;
          return { ...r, isOwnTeam: willBeTenant };
        }),
      );
      // Mark both old + new tenant rows dirty.
      setDirtyUids((prev) => {
        const next = new Set(prev);
        next.add(uid);
        for (const r of draft) if (r.isOwnTeam) next.add(r.uid);
        return next;
      });
    },
    [draft],
  );

  /** Sostituisce l'intera classifica (es. import da screenshot): vecchie righe → deleted, nuove → isNew. */
  const replaceAll = useCallback(
    (rows: StandingDto[]) => {
      const fresh = rows.map((d) => ({ ...dtoToDraft(d), isNew: true }));
      setDraft((prev) => [...prev.map((r) => ({ ...r, isDeleted: true })), ...fresh]);
      setDirtyUids(
        (prev) => new Set([...prev, ...draft.map((r) => r.uid), ...fresh.map((r) => r.uid)]),
      );
    },
    [draft],
  );

  const discardAll = useCallback(() => {
    setDraft(baselineRef.current.map((d) => ({ ...d })));
    setDirtyUids(new Set());
  }, []);

  const dirtyCount = useMemo(() => {
    let n = 0;
    for (const r of draft) if (dirtyUids.has(r.uid)) n += 1;
    return n;
  }, [draft, dirtyUids]);

  return {
    draft,
    visible: useMemo(() => draft.filter((r) => !r.isDeleted), [draft]),
    dirtyUids,
    dirtyCount,
    isDirty: dirtyCount > 0,
    patchRow,
    addRow,
    deleteRow,
    setTenant,
    replaceAll,
    discardAll,
  };
}
